import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import _ from 'lodash';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { compose } from 'redux';

import nds from '@nerdwallet/base-styles/classes';
import { Link, Text } from '@nerdwallet/currency';
import {
  CONTENT_TABS,
  PREHEADER_TYPE,
  DRIVER_GROUPS,
  SkeletonKetoProductCard,
} from '@nerdwallet/react-marketplace-product-card';

import { PRODUCT_TYPES } from '@nerdwallet/marketplace/constants';
import { getApplyLink, getId } from '@nerdwallet/marketplace/selectors/common';
import { getLegacyId as getCreditCardLegacyId } from '@nerdwallet/marketplace/selectors/credit-cards';
import { isNonMonetizing } from '@nerdwallet/marketplace/utils';
import {
  setTrk,
  TrkTag,
  TrkElement,
} from '@nerdwallet/nw-client-lib/trk-helper';
import withDataDependencies from '@nerdwallet/structured-content/with-data-dependencies';
import { withMarketplaceContext } from '@nerdwallet/structured-content/contexts/MarketplaceContext';
import * as compareCreditCardsBarActions from '@nerdwallet/compare-credit-cards-bar/actions';
import { useAnalyticsPageView } from '@nerdwallet/seo-analytics';
import UniversalContainer from '../universal-container/universal-container';
import { addCtaApplyNowLinkTracking } from '../../lib/utils';

import {
  boolCheckboxSpec,
  dataDependencyTypePicker,
  repeatable,
  stringInputSpec,
  stringSelectSpec,
  group,
} from '../../lib/editing-configs';
import withOutOfDateMessaging from '../with-out-of-date-messaging/with-out-of-date-messaging';
import { DATA_DEPENDENCY_TYPES } from '../../lib/constants';
import { getOfferById } from '../../lib/helpers';

import styles from './keto-product-card.module.less';
import { withFilters } from '../filters/with-filters';
import { segmentImpressionId } from '../../lib/segment-analytics';
import KetoStructuredContent from './keto-structured-content';
import KetoUnstructuredContent from './keto-unstructured-content';
import { PRODUCT_TYPE_UNSTRUCTURED_VERTICAL, getProductName } from './utils';

// Omit tabs as needed.
const omittedTabs = [
  // Exclude 'why we like it' tab since it largely duplicates the
  // `why this product` section.
  CONTENT_TABS.WHY_WE_LIKE_IT,
];

const KetoProductCard = ({
  badgeText,
  ctaHref,
  ctaSubtext,
  ctaText,
  data,
  heading,
  identity,
  includeBadge,
  includeWhyThisProduct,
  isUnstructuredVertical,
  keyFacts,
  omitRating,
  productCardImage,
  productType,
  dataDependency,
  starRating,
  whyThisProductMethodologyUrl,
  whyThisProductText,
  whyThisProductTypeOverride,
  saveToCompare,
  removeFromCompare,
  includeCompareButton,
  includeWhyNerdsLoveIt,
  whyNerdsLoveItMethodologyUrl,
  whyNerdsLoveItText,
  identifier,
  location,
  driver,
  container,
  driverOverrides,
  disableDefaultProductCard,
  overrideCtaText,
  overrideProductName,
}) => {
  const offers = Object.values(_.get(data, 'value', {}));
  const loading = _.get(data, 'loading', false);
  const requestedId = _.get(dataDependency, 'data.ids[0].id', null);
  const { pageViewId } = useAnalyticsPageView();
  let marketplaceEntity = getOfferById(requestedId, offers, false);

  const showSkeleton =
    loading ||
    // when is a StructuredVertical
    (!disableDefaultProductCard &&
      !isUnstructuredVertical &&
      !marketplaceEntity) ||
    // when is a UnstructuredVertical
    (!disableDefaultProductCard &&
      isUnstructuredVertical &&
      !heading &&
      !productCardImage &&
      !ctaText &&
      !ctaHref &&
      !ctaSubtext &&
      !keyFacts?.length);

  useEffect(() => {
    // We have to emulate the scroll behavior
    // Try to find element with the same identifier and scroll to it
    const hash = location?.hash.slice(1);
    if (identifier === hash) {
      document.getElementById(identifier)?.scrollIntoView();
    }
  }, [identifier, location?.hash]);

  if (showSkeleton) {
    return (
      <div className={classNames(styles.ketoProductCard)}>
        <SkeletonKetoProductCard />
      </div>
    );
  }

  if (!marketplaceEntity && !isUnstructuredVertical) {
    return null;
  }

  const cardIdentifier = _.isEmpty(marketplaceEntity)
    ? heading
    : getProductName(marketplaceEntity);

  // Allow heading override for BNK.
  const isBanking = dataDependency.type === PRODUCT_TYPES.BANKING;
  const headingProps = isBanking && heading ? { heading } : {};
  const badgeProps = includeBadge
    ? {
        hasPreheader: true,
        preheaderText: badgeText,
        preheaderType: PREHEADER_TYPE.BADGE_AWARDS,
      }
    : {};
  const isComparisonActive = includeCompareButton && !isUnstructuredVertical;

  if (isComparisonActive && marketplaceEntity) {
    // We need to emulate credit cards logic for comparison to match
    marketplaceEntity = {
      ...(marketplaceEntity || {}),
      details: {
        ...(marketplaceEntity?.details || {}),
        legacy_id: marketplaceEntity?.details?.legacy_id?.value
          ? marketplaceEntity?.details?.legacy_id
          : {
              value: marketplaceEntity.id,
              type: typeof marketplaceEntity.id,
            },
      },
    };
  }

  const compareButtonProps = isComparisonActive
    ? {
        _hasCcCompareButton: includeCompareButton,
        /**
         * We have to define handling comparison functionality.
         * The react-components library that we use for KetoProductCard component
         * redefine custom saving logic with an empty function. We need to save and remove
         * the stuff on our side
         */
        // eslint-disable-next-line no-shadow
        _handleCcCompareClick: (_, compared) =>
          compared
            ? // We can just pass card ids. We don't need to load them
              // We use structured data content for the data in the container component
              // See useComparisonPanelProps hook to check the logic
              saveToCompare({
                cards: [{ id: getCreditCardLegacyId(marketplaceEntity) }],
              })
            : removeFromCompare(getCreditCardLegacyId(marketplaceEntity)),
      }
    : {};

  const shouldUpdateSharedId =
    dataDependency.type === DATA_DEPENDENCY_TYPES.CREDIT_CARDS;
  const shouldAddMortgageUtm =
    dataDependency.type === DATA_DEPENDENCY_TYPES.MORTGAGES &&
    !isNonMonetizing(marketplaceEntity);
  const applyLink = getApplyLink(marketplaceEntity);
  const delimeter = applyLink && applyLink.includes('?') ? '&' : '?';
  const prevLinkPropsCheck = shouldUpdateSharedId
    ? {
        ctaHref: `${applyLink}${delimeter}sharedid=4664157C`,
      }
    : {};
  const linkProps = shouldAddMortgageUtm
    ? {
        ctaHref: `${applyLink}${delimeter}partnerParams=utm_campaign%3Dnerdwallet_content`,
      }
    : prevLinkPropsCheck;

  const renderWhyThisProduct = () => (
    <div
      className={classNames([
        styles['why-this-product'],
        nds('margin-bottom-2'),
      ])}
    >
      <Text size="small" component="h4">
        Why this{' '}
        {whyThisProductTypeOverride ||
          getProductName(marketplaceEntity) ||
          'product'}{' '}
        made the list
      </Text>
      <Text component="p">{whyThisProductText}</Text>
      <Link
        target="_blank"
        href={setTrk({
          url: whyThisProductMethodologyUrl,
          params: {
            [TrkTag.copy]: 'Read our methodology',
            [TrkTag.element]: TrkElement.hyperlink,
            [TrkTag.location]: 'KetoProductCard__whyThisProduct',
            trk_productId: getId(marketplaceEntity),
          },
        })}
      >
        Read our methodology
      </Link>
    </div>
  );

  const renderWhyNerdsLoveIt = () => (
    <div
      className={classNames([styles.whyNerdsLoveIt, nds('margin-bottom-2')])}
    >
      <Text size="small" component="h4">
        Why our Nerds love it
      </Text>
      <Text component="p">{whyNerdsLoveItText}</Text>
      <Link
        target="_blank"
        href={setTrk({
          url: whyNerdsLoveItMethodologyUrl,
          params: {
            [TrkTag.copy]: 'Read our methodology',
            [TrkTag.element]: TrkElement.hyperlink,
            [TrkTag.location]: 'KetoProductCard__whyThisProduct',
            trk_productId: getId(marketplaceEntity),
          },
        })}
      >
        Read our methodology
      </Link>
    </div>
  );

  const expandableContentProps = { entityName: 'marketplace_product_card' };

  const analytics = {
    enabled: true,
    // empty object required for product_impression
    productImpressionProps: {},
    // expandableContentProps is duplicated because MPC takes separate props
    // for desktop vs. mobile
    contentProps: { expandableContentProps },
    expandableContentProps,
  };

  let updatedMarketplaceEntity = null;

  if (!isUnstructuredVertical) {
    updatedMarketplaceEntity = addCtaApplyNowLinkTracking(
      marketplaceEntity,
      segmentImpressionId,
      pageViewId
    );
  }

  const baseKetoProps = {
    identifier,
    segmentImpressionId,
    pageViewId,
    analytics,
    identity,
    omitRating,
    omittedTabs,
    badgeProps,
    keyFacts,
    includeWhyThisProduct,
    includeWhyNerdsLoveIt,
    renderWhyThisProduct,
    renderWhyNerdsLoveIt,
    location,
  };

  const structuredKetoProps = {
    styles,
    updatedMarketplaceEntity,
    marketplaceEntity,
    compareButtonProps,
    headingProps,
    linkProps,
    driver,
    driverOverrides,
    ...(overrideProductName && { heading }),
    ...(overrideCtaText && { ctaText }),
  };

  const unstructuredKetoProps = {
    productCardImage,
    cardIdentifier,
    heading,
    ctaHref,
    ctaText,
    ctaSubtext,
    productType,
    starRating,
  };

  return (
    <UniversalContainer {...container}>
      {!isUnstructuredVertical ? (
        <KetoStructuredContent
          {...{ ...baseKetoProps, ...structuredKetoProps }}
        />
      ) : (
        <KetoUnstructuredContent
          {...{ ...baseKetoProps, ...unstructuredKetoProps }}
        />
      )}
    </UniversalContainer>
  );
};

KetoProductCard.displayName = 'KetoProductCard';

KetoProductCard.propTypes = {
  badgeText: PropTypes.string,
  ctaHref: PropTypes.string,
  ctaSubtext: PropTypes.string,
  ctaText: PropTypes.string,
  data: PropTypes.shape({
    loading: PropTypes.bool.isRequired,
    error: PropTypes.object,
    value: PropTypes.object,
  }),
  dataDependency: PropTypes.shape({
    type: PropTypes.string,
    ids: PropTypes.string,
    productPage: PropTypes.string,
  }),
  driver: PropTypes.oneOf([...Object.values(DRIVER_GROUPS)]),
  hasWhyThisProduct: PropTypes.bool,
  heading: PropTypes.string,
  history: PropTypes.object,
  identity: PropTypes.object,
  includeBadge: PropTypes.bool,
  includeWhyThisProduct: PropTypes.bool,
  isUnstructuredVertical: PropTypes.bool,
  keyFacts: PropTypes.arrayOf(PropTypes.string),
  omitRating: PropTypes.bool,
  driverOverrides: PropTypes.bool,
  productCardImage: PropTypes.string,
  productType: PropTypes.oneOf(_.values(PRODUCT_TYPE_UNSTRUCTURED_VERTICAL)),
  starRating: PropTypes.string,
  whyThisProductMethodologyUrl: PropTypes.string,
  whyThisProductText: PropTypes.string,
  whyThisProductTypeOverride: PropTypes.string,
  saveToCompare: PropTypes.func,
  removeFromCompare: PropTypes.func,
  includeCompareButton: PropTypes.bool,
  includeWhyNerdsLoveIt: PropTypes.bool,
  whyNerdsLoveItMethodologyUrl: PropTypes.string,
  whyNerdsLoveItText: PropTypes.string,
  filterByQueryParam: PropTypes.bool,
  queryParamName: PropTypes.string,
  queryParamValue: PropTypes.string,
  identifier: PropTypes.string,
  location: PropTypes.object,
  container: PropTypes.object,
  disableDefaultProductCard: PropTypes.bool,
  overrideCtaText: PropTypes.bool,
  overrideProductName: PropTypes.bool,
};

KetoProductCard.defaultProps = {
  badgeText: '',
  data: {},
  dataDependency: {},
  hasWhyThisProduct: false,
  history: {},
  identity: {},
  includeBadge: false,
  isUnstructuredVertical: false,
  omitRating: false,
  includeCompareButton: false,
};

KetoProductCard.getEditingConfig = (componentDocument) => {
  const isUnstructuredVertical = _.get(
    componentDocument,
    'props.isUnstructuredVertical',
    false
  );
  const isBanking =
    _.get(componentDocument, 'dataDependency.type') === PRODUCT_TYPES.BANKING;
  const isCCType =
    _.get(componentDocument, 'dataDependency.type') ===
    DATA_DEPENDENCY_TYPES.CREDIT_CARDS;
  const headingInputConfig = stringInputSpec(
    'heading',
    'Product Name Override'
  );

  const ctaTextConfig = stringInputSpec('ctaText', 'CTA text');
  const keyFactsInputConfig = repeatable(
    'keyFacts',
    'Key Facts',
    'keyFact',
    () => [
      stringInputSpec('label', 'Key Fact Label'),
      stringInputSpec('text', 'Key Fact Text'),
      stringInputSpec('tooltip', 'Key Fact Tooltip'),
    ]
  );
  const unstructuredVerticalConfig = [
    stringSelectSpec(
      'productType',
      'Product type',
      _.map(PRODUCT_TYPE_UNSTRUCTURED_VERTICAL, (productType) => ({
        label: _.startCase(productType),
        value: productType,
      }))
    ),
    headingInputConfig,
    stringInputSpec('productCardImage', 'Product card image'),
    stringInputSpec('ctaHref', 'CTA href'),
    ctaTextConfig,
    stringInputSpec('ctaSubtext', 'CTA subtext'),
    keyFactsInputConfig,
  ];

  const dataDependencyIdInputConfig = stringInputSpec(
    'dataDependency.data.ids.0.id',
    'Offer Id',
    { isProp: false }
  );

  const dataDependencyDriver = stringSelectSpec(
    'driver',
    'Driver',
    [
      ...Object.values(DRIVER_GROUPS).map((item) => ({
        label: item.replace('CC_', '')?.split('_')?.join(' '),
        value: item,
      })),
    ],
    {
      help: `Defaults to "${DRIVER_GROUPS.DEFAULT_DRIVER_GROUP}"`,
    }
  );

  const dataDependencyDriverOverrides = boolCheckboxSpec(
    'driverOverrides',
    'Driver Overrides?'
  );
  const overrideProductNameInputConfig = _.get(
    componentDocument,
    'props.overrideProductName',
    false
  )
    ? headingInputConfig
    : [];

  const overrideCtaTextInputConfig = _.get(
    componentDocument,
    'props.overrideCtaText',
    false
  )
    ? ctaTextConfig
    : [];

  const driverOverridesKeyFacts = _.get(
    componentDocument,
    'props.driverOverrides',
    false
  )
    ? keyFactsInputConfig
    : [];

  const dataDependencyConfig = [
    dataDependencyTypePicker,
    dataDependencyIdInputConfig,
    boolCheckboxSpec('overrideProductName', 'Override Product Name?'),
    overrideProductNameInputConfig,
    boolCheckboxSpec('overrideCtaText', 'Override CTA text?'),
    overrideCtaTextInputConfig,
    isCCType ? dataDependencyDriver : [],
    dataDependencyDriverOverrides,
    driverOverridesKeyFacts,
    !isBanking &&
      boolCheckboxSpec('includeCompareButton', 'Add Compare Button?'),
  ];
  const includeBadge = _.get(componentDocument, 'props.includeBadge', false);
  const badgeConfig = [
    boolCheckboxSpec('includeBadge', 'Include Best-of Badge?'),
    includeBadge && stringInputSpec('badgeText', 'Badge Text'),
  ];

  const includeWhyThisProduct = _.get(
    componentDocument,
    'props.includeWhyThisProduct',
    false
  );
  const whyThisProductConfig = [
    boolCheckboxSpec(
      'includeWhyThisProduct',
      "Include 'Why this product' section?",
      {
        help: 'Include a section below the product card explaining why this product is a Best of Award',
      }
    ),
    ...(includeWhyThisProduct
      ? [
          stringInputSpec('whyThisProductText', 'Product explanation', {
            help: 'Short text explaining why this product made the list',
          }),
          stringInputSpec('whyThisProductMethodologyUrl', 'Methodology URL'),
          stringInputSpec(
            'whyThisProductTypeOverride',
            'Override product type',
            {
              help: "Override the category name to use in the 'Why this product' title",
            }
          ),
        ]
      : []),
  ];
  // We use it in withFilters HOC
  const queryParamsConfig = [
    boolCheckboxSpec('filterByQueryParam', 'Reflect on query params changes?', {
      help: 'If a user uses a different query param we will hide the card',
    }),
    ...(_.get(componentDocument, 'props.filterByQueryParam', false)
      ? [
          stringInputSpec('queryParamName', 'Query param name', {
            help: 'Which query param we should use',
          }),
          stringInputSpec('queryParamValue', 'Query param value', {
            help: `
              The list of the possible values separated by |. When a queryParam with the queryParamName
              contains one of the possible values we show the entity
            `,
          }),
        ]
      : []),
  ];

  const includeWhyNerdsLoveIt = _.get(
    componentDocument,
    'props.includeWhyNerdsLoveIt',
    false
  );
  const whyNerdsLoveItConfig = [
    boolCheckboxSpec(
      'includeWhyNerdsLoveIt',
      "Include 'Why our Nerds Love It' section?",
      {
        help: 'Include a section below the product card explaining why our Nerds Love it',
      }
    ),
    ...(includeWhyNerdsLoveIt
      ? [
          stringInputSpec('whyNerdsLoveItText', 'Explanation', {
            help: 'Short text explaining why our Nerds Love It',
          }),
          stringInputSpec('whyNerdsLoveItMethodologyUrl', 'Methodology URL'),
        ]
      : []),
  ];

  return [
    boolCheckboxSpec(
      'disableDefaultProductCard',
      'Disable Default Product Card?'
    ),
    ...UniversalContainer.getEditingConfig(
      componentDocument,
      'Container',
      [
        'backgroundColor',
        'backgroundImage',
        'border',
        'margin',
        'padding',
        'hide',
      ],
      'container'
    ),
    group('Product info', [
      boolCheckboxSpec(
        'isUnstructuredVertical',
        'Is This An Unstructured Vertical?',
        {
          help: 'Check to manually input information for a vertical without data in marketplace (Taxes, Travel, Auto Insurance)',
        }
      ),
      // Show 1. unstructured vertical fields or 2. data dependency fields.
      ...(isUnstructuredVertical
        ? unstructuredVerticalConfig
        : dataDependencyConfig),
      // Show heading override field if BNK and not already showing for unstructured vertical.
      !isUnstructuredVertical && isBanking && headingInputConfig,
    ]),
    group('Ratings + badges', [
      !isUnstructuredVertical &&
        boolCheckboxSpec('omitRating', 'Omit star rating?'),
      isUnstructuredVertical && stringInputSpec('starRating', 'Star rating'),
      ...badgeConfig,
    ]),
    group('Card footer', [...whyThisProductConfig, ...whyNerdsLoveItConfig]),
    group('Jump link', [
      stringInputSpec('identifier', 'Jump link ID', {
        help: 'You can specify a unique Jump Link ID for this product card to navigate directly to the spot on the page where this card appears',
      }),
    ]),
    group('Filters', [...queryParamsConfig]),
  ].filter(Boolean); // Remove empty/falsy values to ensure test parity.
};

const dataDependencies = (props) => {
  const type = _.get(props, 'dataDependency.type');
  if (!type) {
    return null;
  }

  const id = _.get(props, 'dataDependency.data.ids[0].id');
  const ids = id ? [id] : [];

  return {
    data: {
      type,
      ids,
    },
  };
};

const mapDispatchToProps = {
  saveToCompare: compareCreditCardsBarActions.saveToCompare,
  removeFromCompare: compareCreditCardsBarActions.removeFromCompare,
};

const WrappedComponent = compose(
  withFilters,
  withMarketplaceContext,
  withOutOfDateMessaging,
  connect(
    createStructuredSelector({
      size: _.property('mediaQuery'),
      identity: _.property('identity'),
    }),
    mapDispatchToProps
  )
)(KetoProductCard);

export default withDataDependencies(WrappedComponent, dataDependencies);
